"use client";

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { PricingModal } from "@/components/pricing/PricingModal";
import { captureClientEvent } from "@/lib/analytics/client";

export interface PaywallContextValue {
  openPaywall: (options?: { defaultTab?: "subscription" | "payg"; trigger?: string; context?: Record<string, unknown> }) => void;
  closePaywall: () => void;
}

const PaywallContext = createContext<PaywallContextValue | null>(null);

export interface PaywallProviderProps {
  children: React.ReactNode;
}

export function PaywallProvider({ children }: PaywallProviderProps) {
  const [open, setOpen] = useState(false);
  const [defaultTab, setDefaultTab] = useState<"subscription" | "payg">("subscription");
  const [trigger, setTrigger] = useState<string | undefined>(undefined);
  const [extraContext, setExtraContext] = useState<Record<string, unknown> | undefined>(undefined);

  const openPaywall = useCallback((options?: { defaultTab?: "subscription" | "payg"; trigger?: string; context?: Record<string, unknown> }) => {
    if (options?.defaultTab) setDefaultTab(options.defaultTab);
    setTrigger(options?.trigger);
    setExtraContext(options?.context);
    captureClientEvent("paywall_opened", {
      trigger: options?.trigger ?? "unknown",
      defaultTab: options?.defaultTab ?? "subscription",
      ...options?.context,
    });
    setOpen(true);
  }, []);

  const closePaywall = useCallback(() => setOpen(false), []);

  const value = useMemo(() => ({ openPaywall, closePaywall }), [openPaywall, closePaywall]);

  useEffect(() => {
    if (!open) {
      setTrigger(undefined);
      setExtraContext(undefined);
    }
  }, [open]);

  return (
    <PaywallContext.Provider value={value}>
      {children}
      <PricingModal open={open} setOpen={setOpen} defaultTab={defaultTab} trigger={trigger} context={extraContext} />
    </PaywallContext.Provider>
  );
}

export function usePaywall(): PaywallContextValue {
  const ctx = useContext(PaywallContext);
  if (!ctx) throw new Error("usePaywall must be used within <PaywallProvider />");
  return ctx;
}
